import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Brand } from "../../pages/playground/types";

interface BrandAssociationDialogProps {
  open: boolean;
  organizationId: string;
  organizationName: string;
  onClose: (refresh?: boolean) => void;
  onAssociate: (brandId: string) => Promise<void>;
}

export const BrandAssociationDialog: React.FC<
  BrandAssociationDialogProps
> = ({ open, organizationName, onClose, onAssociate }) => {
  const [unassociatedBrands, setUnassociatedBrands] = useState<Brand[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isAssociating, setIsAssociating] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUnassociatedBrands();
      setSelectedBrandIds(new Set());
    }
  }, [open]);

  const fetchUnassociatedBrands = async () => {
    setIsLoading(true);
    try {
      const axios = (await import("../../api")).default;

      const response = await axios.get("/brand", {
        params: {
          page: 1,
          unassociated: true,
        },
      });

      const brands = response?.data?.data?.items || [];
      setUnassociatedBrands(brands);
    } catch (error) {
      console.error("Error fetching unassociated brands:", error);
      setUnassociatedBrands([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBrand = (brandId: string) => {
    setSelectedBrandIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(brandId)) {
        newSet.delete(brandId);
      } else {
        newSet.add(brandId);
      }
      return newSet;
    });
  };

  const handleAssociate = async () => {
    if (selectedBrandIds.size === 0) return;

    setIsAssociating(true);
    try {
      await Promise.all(
        Array.from(selectedBrandIds).map((brandId) => onAssociate(brandId))
      );
      onClose(true);
    } catch (error) {
      console.error("Error associating brands:", error);
      alert("Failed to associate brands. Please try again.");
    } finally {
      setIsAssociating(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Associate Brands to {organizationName}</DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : unassociatedBrands.length === 0 ? (
          <Box py={2}>No unassociated brands available.</Box>
        ) : (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
            }}
          >
            {unassociatedBrands.map((brand) => (
              <FormControlLabel
                key={brand.id}
                control={
                  <Checkbox
                    checked={selectedBrandIds.has(brand.id || "")}
                    onChange={() => handleToggleBrand(brand.id || "")}
                  />
                }
                label={brand.name || ""}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={isAssociating}>
          Cancel
        </Button>
        <Button
          onClick={handleAssociate}
          variant="contained"
          disabled={selectedBrandIds.size === 0 || isAssociating}
        >
          {isAssociating ? "Associating..." : `Associate (${selectedBrandIds.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
